"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackTikTokClientEvent, trackTikTokPageView } from "@/lib/tracking/tiktokClient";
import { resolveTikTokContent } from "@/lib/tracking/tiktokContent";

// Drives the two route-scoped TikTok signals: pageviews and ViewContent.
//
// Pageview — the base pixel snippet (TikTokPixel.tsx) calls ttq.page() once when
// the script loads. Everything after that is a Next.js soft navigation: the URL
// changes, the document does not reload, and the pixel never hears about it.
// That left TikTok seeing a single pageview per session (the ad landing),
// starving view-through attribution and retargeting audiences of the in-app
// journey. So we fire page() on every path CHANGE, and skip the first path —
// the snippet already reported it, and firing again would double-count the
// landing page.
//
// ViewContent — a pre-purchase intent signal, fired only on selling surfaces
// (see resolveTikTokContent). Unlike page(), this one MUST also fire on the
// first path: the snippet never sends ViewContent, so skipping the initial
// render would lose it on exactly the ad landing that matters most.
//
// Mounted once at the app root (app/layout.tsx). No-ops wherever the pixel
// isn't loaded; calls made before the pixel script arrives are queued by
// tiktokClient rather than dropped.
export function TikTokPageView() {
  const pathname = usePathname();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    const isFirst = lastPathRef.current === null;
    if (!isFirst && lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;

    if (!isFirst) trackTikTokPageView();

    const content = resolveTikTokContent(pathname);
    if (!content) return;
    // No value/currency: a tool or pricing page view has no transaction amount,
    // and sending 0 would be scored as a zero-value view rather than an unpriced
    // one.
    trackTikTokClientEvent("ViewContent", {
      contents: [
        {
          content_id: content.contentId,
          content_type: "product",
          content_name: content.contentName,
        },
      ],
    });
  }, [pathname]);

  return null;
}
